package dev.lingvo.site.nav

import dev.lingvo.shared.localeInfo

/**
 * What a page acts on.
 *
 * **The shell had no answer to this and drew chrome as if every page were a project page.** A
 * project switcher was the most prominent control in the sidebar while sixteen of the app's
 * fifty-seven routes are project-scoped; on the other forty-one it sat there reading "Select a
 * project", which is an interface admitting its own model is wrong.
 */
enum class AppScope {
    /** Acts on one project: its strings, its languages, its QA, its settings. */
    PROJECT,

    /** Acts on the workspace: members, billing, providers, agents, the audit log. */
    WORKSPACE,

    /** Acts on the person: their account, their security, their tokens. */
    USER,
}

/**
 * One segment of the location trail, and where it can take you.
 *
 * [href] is always a real destination, because every row of every picker built from these has to be
 * an `<a href>`. A `<select>` that navigates on change is a WCAG 3.2.2 change-of-context failure —
 * arrowing through the options *commits* each one — and it silently removes middle-click,
 * open-in-new-tab, the status-bar URL preview, and the link lists screen readers build. The old
 * project switcher was exactly that control, doing exactly that job.
 */
data class TrailCrumb(
    val label: String,
    val href: String?,
    /** True when this crumb offers alternatives — a workspace, a project, a language. */
    val switchable: Boolean = false,
)

/**
 * The pure route model behind the shell's chrome.
 *
 * Pure on purpose: every question the trail asks is answerable from the path alone, so it can be
 * tested without a browser, and the chrome cannot disagree with the URL the way three separate
 * readers of "which workspace am I in" used to.
 */
object AppRoute {

    fun scopeOf(path: String): AppScope {
        val segments = segments(path)
        return when {
            segments.getOrNull(1) == "projects" && segments.size > 2 -> AppScope.PROJECT
            segments.getOrNull(1) == "account" -> AppScope.USER
            segments.getOrNull(1) == "settings" && segments.getOrNull(2) == "tokens" -> AppScope.USER
            segments.getOrNull(1) == "settings" -> AppScope.WORKSPACE
            segments.getOrNull(1) in setOf("reports", "workspaces") -> AppScope.WORKSPACE
            else -> AppScope.WORKSPACE
        }
    }

    /**
     * Whether what this path renders depends on which workspace is selected.
     *
     * Used to decide whether a workspace changing in another tab is worth telling this one about.
     * Derived from [scopeOf] rather than from a list of routes, so a page added tomorrow is covered
     * by default and has to opt *out* deliberately — the opposite of the arrangement that left
     * seventeen finished pages with no inbound link.
     *
     * **Anchored to `/app` first, because [scopeOf] is not a total function over the site.** Its
     * `else` branch answers `WORKSPACE`, so without this guard `/`, `/pricing` and `/login` all
     * claim to read the selection. Nothing would notice today — the shell is the only caller — but a
     * predicate that is wrong everywhere except at its one call site is a trap for the second one.
     *
     * `PROJECT` counts as much as `WORKSPACE`: the boundary this traces is the one
     * `AppTrail.kt:89-91` already draws, where the workspace crumb renders on both and on neither
     * USER route. A project page shows that crumb, so a reader of that page can be looking at a
     * workspace name that is no longer the selected one.
     *
     * `/app/workspaces` is carved out because it is the page for choosing among workspaces. Telling
     * someone their selection changed, on the screen whose job is changing it, is noise.
     */
    fun readsSelectedWorkspace(path: String): Boolean {
        val segments = segments(path)
        if (segments.getOrNull(0) != "app") return false
        if (segments.getOrNull(1) == "workspaces") return false
        return scopeOf(path) != AppScope.USER
    }

    /** The project a path is about, or null. `new` is a form, not a project. */
    fun projectSlug(path: String): String? {
        val segments = segments(path)
        if (segments.getOrNull(1) != "projects") return null
        return segments.getOrNull(2)?.takeIf { it != "new" }
    }

    /**
     * The part of a project route after its slug: `keys`, `translate/de`, `settings/qa`.
     *
     * This is what makes switching keep your place. Choosing another project used to throw the
     * sub-page away and land you on its overview, so every switch cost two clicks to get back to
     * the screen you were already looking at.
     */
    fun projectSection(path: String): String? {
        val segments = segments(path)
        if (segments.getOrNull(1) != "projects" || segments.size <= 3) return null
        return segments.drop(3).joinToString("/")
    }

    // `projectNavigationId(path)` used to live here: it mapped a path onto the rail destination that
    // should light up, for a `ProjectNav` component that no longer exists. `appRailDestinationActive`
    // in the shell answers the same question directly from the destination's own path, so the mapping
    // was a second, hand-maintained copy of the route table with nothing reading it. Deleted with its
    // three tests rather than kept "in case": a table nothing consults is a table nothing keeps right.

    /**
     * Where switching from [path] to [target] should land.
     *
     * **Carried where it is well-defined, dropped where it is a guess.** A section that means the
     * same thing in any project travels; one that names a language or an id does not, because the
     * other project may not have that language and certainly does not have that key. Landing
     * somebody on a 404 to preserve the illusion of continuity is worse than landing them on the
     * project they asked for.
     */
    fun retarget(path: String, target: String): String {
        val section = projectSection(path) ?: return "/app/projects/$target"
        val head = section.substringBefore('/')
        return when {
            head in PORTABLE_SECTIONS -> "/app/projects/$target/$section"
            // `translate/de` and `review/de` name a language the target may not have; `strings/{id}`
            // and `data/jobs/{id}` name rows that exist in exactly one project.
            head in LOCALE_SECTIONS -> "/app/projects/$target"
            else -> "/app/projects/$target"
        }
    }

    /** True when switching project will keep you on the same kind of page. */
    fun carriesSection(path: String): Boolean =
        projectSection(path)?.substringBefore('/') in PORTABLE_SECTIONS

    /**
     * A human label for a path segment.
     *
     * The breadcrumb used to print raw URL segments, so a locale rendered as "De" and a project as
     * its slug — the two things on that row somebody most needs to read.
     */
    fun label(segment: String): String = when (segment) {
        "qa" -> "QA"
        "mt" -> "Machine translation"
        "tokens" -> "API tokens"
        "import-export" -> "Import and export"
        "keys" -> "Keys"
        else -> {
            // A locale segment names a language; say the language.
            val info = localeInfo(segment)
            if (info.englishName != segment) {
                info.englishName
            } else {
                segment.replace('-', ' ').replaceFirstChar { it.uppercase() }
            }
        }
    }

    /** The section crumb: what this page is, within its scope. */
    fun sectionLabel(path: String): String? {
        val segments = segments(path)
        return when (scopeOf(path)) {
            AppScope.PROJECT -> projectSection(path)?.let { section ->
                section.split('/').joinToString(" · ") { label(it) }
            }
            AppScope.WORKSPACE, AppScope.USER -> segments.drop(2).takeIf { it.isNotEmpty() }
                ?.joinToString(" · ") { label(it) }
                ?: segments.getOrNull(1)?.let { label(it) }
        }
    }

    private fun segments(path: String): List<String> = path.split('/').filter(String::isNotBlank)

    /** Sections that mean the same thing in every project, so a switch can carry them. */
    private val PORTABLE_SECTIONS = setOf(
        "keys", "qa", "glossary", "data", "import-export", "pretranslate", "proposals", "settings",
        "stores",
    )

    /** Sections whose path names a language or a row id, which another project need not have. */
    private val LOCALE_SECTIONS = setOf("translate", "review", "strings")
}
